import { ChangeEvent, forwardRef } from 'react'

import SearchIcon from 'src/components/ui/atoms/Icons/SearchIcon'
import { cn } from 'src/lib/cn'
import HtmlProps from 'src/types/HtmlProps'
import ComponentColor from 'src/types/ItemColor'
import ButtonClose from '../ButtonClose/ButtonClose'

import * as styles from './InputCompletion.scss'

export interface InputCompletionProps extends Omit<HtmlProps<HTMLInputElement>, 'onChange'> {
  inputValue?: string
  isFocused?: boolean
  placeHolder?: string
  position?: 'left' | 'right'
  onClear?: () => void
  componentColor?: ComponentColor
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
}

const InputCompletion = forwardRef<HTMLInputElement, InputCompletionProps>(
  (
    {
      onFocus,
      placeHolder,
      isFocused,
      onChange,
      inputValue,
      className,
      position,
      onClear,
      componentColor,
      ...inputProps
    },
    ref,
  ) => {
    const isOrange = componentColor === ComponentColor.orange
    const isPurple = componentColor === ComponentColor.purple

    const inputClasses = cn(
      {
        [styles.InputCompletion]: true,
        [styles.InputCompletionActive]: isFocused,
        [styles.InputCompletionActiveOrange]: isOrange && isFocused,
        [styles.InputCompletionActivePurple]: isPurple && isFocused,
        [styles.InputLeft]: position === 'left',
        [styles.InputRight]: position === 'right',
      },
      className,
    )

    const hasValue = Boolean(inputValue?.toString().length)

    return (
      <div className={inputClasses} id="search_bar">
        <SearchIcon
          data-testid="input-completion-search-icon"
          className={cn({
            [styles.Icon]: true,
            [styles.IconOrange]: isOrange,
            [styles.IconPurple]: isPurple,
          })}
          height="18"
          width="18"
        />

        <input
          type="text"
          className={cn({
            [styles.InputElement]: true,
            [styles.InputElementOrange]: isOrange,
            [styles.InputElementPurple]: isPurple,
          })}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={placeHolder}
          value={inputValue}
          data-testid="input-completion-field"
          ref={ref}
          {...inputProps}
        />

        {onClear && hasValue && (
          <ButtonClose aria-label="Clear input" onClick={onClear} className={styles.CloseButton} />
        )}
      </div>
    )
  },
)

InputCompletion.displayName = 'InputCompletion'

export default InputCompletion
